import { Autocomplete, Box, Button, Checkbox, TextField } from "@mui/material";
import { TotalAmountOfAssociatedOrders } from "./TotalAmountOfAssociatedOrders";
import { renderOrderSelectedOption } from "./renderOrderSelectedOption";
import { batchAssociateOrdersToTransaction } from "./associateOrderToTransaction";
import { RenderOrderAssociateOption } from "./RenderOrderAssociateOption";
import { useContext, useState } from "react";
import { RelatedRecordsContext } from "../../../components/recordsRelated/RecordsRelated";
import { RecordContext } from "../../../components/record/Record";
import { ORDERS_SINCE_DATE } from "../../../utils/constants";
import { useForm } from "react-hook-form";
import { ClinicsDropdown } from "../../../components/general-dropdowns/clinics-dropdown/ClinicsDropdown";
import { useField } from "../../../api/useField";
import { renderFieldInput } from "../../../components/fieldsInputs/renderFieldInput";
import { useRecords } from "../../../api/useRecords";
import { useUpdateRecord } from "../../../api/useUpdateRecord";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

export type BankTransaction = {
    id: string | number;
    amount: string;
    category: {
        id: number;
    };
};

const ORDERS_FIELDS_TO_QUERY =
    "id,amount,payment_method,payment_date,order.id,order.order_date,order.order_items.*,order.treatment.client.*,order.treatment.client.user_id.*";

export const OrderAssociateComponent = () => {
    const recordContext = useContext(RecordContext);
    const relatedRecordsContext = useContext(RelatedRecordsContext);
    const orders = relatedRecordsContext?.items;

    const filterForm = useForm();
    const selectedClinic = filterForm.watch("clinic");
    const selectedPaymentMethod = filterForm.watch("payment_method");

    const { formatedField: paymentMethodField } = useField("orders_payments", "payment_method");

    const [selectedOrders, setSelectedOrders] = useState<Array<any>>([]);

    const ordersFilter = {
        _and: [
            { connected_bank_transaction_id: { _null: true } },
            { payment_date: { _gte: ORDERS_SINCE_DATE } },
            { order: { clinic: { _eq: selectedClinic } } },
            { payment_method: { _eq: selectedPaymentMethod } },
        ],
    };

    const shouldFetchOrders = Boolean(selectedClinic && selectedPaymentMethod);

    const { data: ordersData, isInitialLoading: isLoadingOrders } = useRecords(
        "orders_payments",
        ORDERS_FIELDS_TO_QUERY,
        {
            extraOptions: {
                filter: ordersFilter,
                sort: "-payment_date",
                pageSize: 100,
            },
            enabled: shouldFetchOrders,
        }
    );

    const availableOrders = shouldFetchOrders ? (ordersData?.items || []) : [];

    const updateRecord = useUpdateRecord({ queryInvalidatePrefix: `${relatedRecordsContext?.entity}` });

    const handleAssociate = async () => {
        const bankTransactionId = recordContext?.id;
        if (!bankTransactionId || selectedOrders.length === 0) return;

        const orderPaymentIds = selectedOrders.map((order) => order.id);
        await batchAssociateOrdersToTransaction(orderPaymentIds, bankTransactionId, updateRecord);
        setSelectedOrders([]);
    };

    const allSelected = availableOrders.length > 0 && selectedOrders.length === availableOrders.length;

    const handleToggleAll = () => {
        const newSelection = allSelected ? [] : availableOrders;
        setSelectedOrders(newSelection);
    };

    return (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {orders && recordContext?.item && (
                <TotalAmountOfAssociatedOrders
                    bankTransaction={recordContext?.item as BankTransaction}
                    orders={orders}
                    type="detail"
                />
            )}
            <Box sx={{ width: 200 }}>
                <ClinicsDropdown entityForm={filterForm} value="" />
            </Box>
            {selectedClinic && paymentMethodField && (
                <Box sx={{ width: 200 }}>
                    {renderFieldInput(paymentMethodField, "", "filter", filterForm)}
                </Box>
            )}
            {shouldFetchOrders && (
                <>
                    <Autocomplete
                        multiple
                        limitTags={1}
                        sx={{ width: 450, "& .MuiAutocomplete-inputRoot": { flexWrap: "nowrap" }, "& .MuiAutocomplete-tag": { my: 0 } }}
                        options={availableOrders}
                        loading={isLoadingOrders}
                        value={selectedOrders}
                        onChange={(_, newValue) => setSelectedOrders(newValue)}
                        getOptionLabel={(option) => renderOrderSelectedOption(option)}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        disableCloseOnSelect
                        renderOption={(props, option, { selected }) => (
                            <li {...props} key={option.id}>
                                <Checkbox
                                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                                    checked={selected}
                                    sx={{ mr: 1 }}
                                />
                                <RenderOrderAssociateOption option={option} props={{}} />
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} label="Compras" placeholder="Selecione as compras" />
                        )}
                    />
                    <Button variant="text" size="small" onClick={handleToggleAll}>
                        {allSelected ? "Desselecionar" : "Selecionar tudo"}
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleAssociate}
                        disabled={selectedOrders.length === 0 || updateRecord.isLoading}
                    >
                        Associar
                    </Button>
                </>
            )}
        </Box>
    );
};
